import React, { useContext, useEffect } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';

import PageTemplate from '../../templates/PageTemplate';
import { DocumentTitle } from '../../components/atoms';
import { StepContext, steps, Step } from '../../contexts/StepContext';
import { useStyles } from './ProcessingStyles';
import { determineAvailableOptions, populateComparisonTableWithPricing } from '../../utils/coverDetailsUtils';

export const Processing: React.FC = () => {
  const { updateShowStepper, updateData, updateActiveStep, updateShowHero } = useContext(StepContext);
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      history.replace('/');
      return;
    }

    window.scrollTo(0, 0);
    updateActiveStep(5);
    updateShowStepper(false);
    updateShowHero(false);

    if (location.pathname === '/cancel/') {
      expire();
    } else {
      ingest();
    }
  }, []);

  const retrieveQuoteData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_RETRIEVE_QUOTE_ENDPOINT
        }/${sessionId}/${Cookies.get('GFSessionControl')}`,
      );

      if (response.data.err) throw new Error(response.data.err);

      const retrievedData = response.data?.data;

      const currentEpochTime = Math.floor(Date.now() / 1000);
      if (retrievedData.ttlTimeout && retrievedData.ttlTimeout < currentEpochTime) {
        history.replace('/timeout');
        return null;
      }

      const quoteDataReactFormat = {
        timeout: false,
        quote: retrievedData.quote_id,
        firstName: retrievedData.customer.first_name,
        lastName: retrievedData.customer.last_name,
        emailAddress: retrievedData.customer.email,
        phoneNumber: retrievedData.customer.telephone,
        addressLine1: retrievedData.customer.address.house,
        addressLine2: retrievedData.customer.address.street,
        addressLine3: '',
        addressLine4: retrievedData.customer.address.town,
        postcode: retrievedData.customer.address.postcode,
        registrationNumber: retrievedData.vehicle.registration,
        vehicleMake: retrievedData.vehicle.make,
        vehicleModel: retrievedData.vehicle.model,
        vehicleYear: retrievedData.vehicle.year_of_manufacture,
        productName: '',
        personalCover: retrievedData.cover_options.additional_options.includes('Personal') ? 'True' : 'False',
        homeCall: retrievedData.homeCall,
        baseOption: baseOptionForFrontend(retrievedData.cover_options.base_option),
        coverPrice: retrievedData.price_pence / 100,
        policyStartDate: retrievedData.start_date,
        policyEndDate: retrievedData.end_date,
        automaticRenewal: retrievedData.continuous,
        coverType: baseOptionForFrontend(retrievedData.cover_options.base_option),
        additionalOptions: retrievedData.cover_options.additional_options,
        quoteTotal: 0,
        policyType: '',
        motorHomeCampervan: retrievedData.motorHomeCampervan,
        carCaravan: retrievedData.carCaravan,
        carMotorbike: retrievedData.carMotorbike,
        coverAtHomeResults: retrievedData.coverAtHomeResults,
        vehiclesToCover: 'Selected',
        availableOptionsFromSelections: retrievedData.availableOptionsFromSelections,
        checkoutId: sessionId,

        vehicleLookUp: {
          vehicleMake: retrievedData.vehicle.make,
          vehicleModel: retrievedData.vehicle.model,
          vehicleYear: retrievedData.vehicle.year_of_manufacture,
          registrationNumber: retrievedData.vehicle.registration,
        },

        postcodeLookup: {
          firstLineOfAddress: retrievedData.customer.address.house,
          secondLineOfAddress: retrievedData.customer.address.street,
          town: retrievedData.customer.address.town,
          postcode: retrievedData.customer.address.postcode,
        },

        ...(retrievedData.partner
          ? {
            partnerInitial: retrievedData.partner.initial,
            partnerSurname: retrievedData.partner.surname,
          }
          : {}),

        ...(retrievedData.cover_options.additional_options.includes('AdditionalVehicle')
          ? {
            numberOfVehicles: 'Dual',
            additionalVehicleMake: retrievedData.additional_vehicle.make,
            additionalVehicleModel: retrievedData.additional_vehicle.model,
            additionalVehicleRegistrationNumber: retrievedData.additional_vehicle.registration,
            additionalVehicleYear: retrievedData.additional_vehicle.year_of_manufacture,

            additionalVehicleLookUp: {
              additionalVehicleMake: retrievedData.additional_vehicle.make,
              additionalVehicleModel: retrievedData.additional_vehicle.model,
              additionalVehicleYear: retrievedData.additional_vehicle.year_of_manufacture,
              additionalVehicleRegistrationNumber: retrievedData.additional_vehicle.registration,
            },
          }
          : {
            numberOfVehicles: 'Single',
          }),
      };

      const stateDataObject = await getPricingData(quoteDataReactFormat);
      updateData(stateDataObject);
      return quoteDataReactFormat;
    } catch (error) {
      history.replace('/timeout');
      return null;
    }
  };

  function baseOptionForFrontend(baseOption: string): string {
    if (baseOption === 'PremiumUKPlus') {
      return 'Premium UK PLUS';
    } else if (baseOption === 'PremiumUK') {
      return 'Premium UK';
    } else if (baseOption === 'RoadsideRecovery') {
      return 'Roadside & Recovery';
    }
    return '';
  }

  const getPricingData = async (quoteData) => {
    try {
      const { data: options } = await axios.get(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_OPTIONS_ENDPOINT}`,
      );

      const stateDataObjectInternal = {
        ...quoteData,
        availableOptionsFromSelections: determineAvailableOptions(
          quoteData.homeCall,
          quoteData.numberOfVehicles,
          quoteData.motorHomeCampervan,
          quoteData.carCaravan,
          quoteData.carMotorbike,
          quoteData.coverAtHomeResults === 'First' ? true : false,
        ),
        options: options.Items,
        priceData: populateComparisonTableWithPricing(options.Items),
      };
      return stateDataObjectInternal;
    } catch {
      history.push('/');
      return null;
    }
  };

  const ingest = async () => {
    const quote = await retrieveQuoteData();
    if (!quote) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_INGEST_ENDPOINT}`,
        { checkoutId: quote?.checkoutId || sessionId },
      );

      if (response.status === 200) {
        history.push('/all-sorted');
      } else {
        history.push('/failedPayment');
      }
    } catch {
      history.push('/failedPayment');
    }
  };

  const expire = async () => {
    const quote = await retrieveQuoteData();
    if (!quote) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVERLESS_BASE_URL}/${process.env.REACT_APP_EXPIRE_CHECKOUT_ENDPOINT}`,
        { checkoutId: quote?.checkoutId || sessionId },
      );
    } catch {
      // We dont care if this succeeds or fails. 
    }

    updateActiveStep(4);
    history.push(steps[Step.LETS_REVIEW].url);
  };

  return (
    <PageTemplate>
      <DocumentTitle title={`DLG ${process.env.REACT_APP_SITE_ID?.toUpperCase()} - Processing`} />
      <Grid container className={classes.gridMainContainer}>
        <Grid item xs={12} sm={5} lg={6} className={classes.gridMain}>
          <Typography className={classes.heading} variant="h1">
            We&apos;re nearly done
          </Typography>
          <Typography className="pt1">Please don&apos;t close this window or click back in the browser.</Typography>
        </Grid>
      </Grid>
    </PageTemplate>
  );
};

export default Processing;
